import { useEffect, useState } from 'react'
import { Download, RefreshCw } from 'lucide-react'
import type { UpdateStatus } from '@/types/electron'

export default function UpdateNotice() {
  const [status, setStatus] = useState<UpdateStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!window.electronAPI?.isElectron?.()) return
    const unsubscribe = window.electronAPI.onUpdateStatus((data) => {
      setStatus(data)
      if (data.status === 'available') setDismissed(false)
    })
    return unsubscribe
  }, [])

  if (!status || status.status === 'error') return null

  // Mandatory updates block everything — no way to dismiss, no way to keep using the app.
  if (status.mandatory) {
    return (
      <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-center gap-6 bg-black/95 text-white">
        <div className="h-14 w-14 animate-spin rounded-full border-4 border-white/20 border-t-white" />
        <div className="flex flex-col items-center gap-2 text-center px-6">
          <div className="text-xl font-bold tracking-wide">
            {status.status === 'downloaded' ? 'Installing update...' : 'Required update in progress'}
          </div>
          <div className="text-sm text-white/50">
            {status.status === 'downloading' && status.percent !== undefined
              ? `Downloading v${status.version} — ${status.percent}%`
              : status.status === 'downloaded'
                ? 'The app will restart automatically.'
                : `Preparing v${status.version}...`}
          </div>
        </div>
      </div>
    )
  }

  // Optional update — a dismissible corner prompt, never blocks the booth/admin UI underneath.
  if (dismissed) return null

  return (
    <div className="fixed bottom-6 right-6 z-[10000] w-80 rounded-2xl border border-white/10 bg-[#14141c] p-4 shadow-2xl text-white">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          {status.status === 'downloading' ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {status.status === 'available' && `Update available${status.version ? ` (v${status.version})` : ''}`}
            {status.status === 'downloading' && `Downloading update... ${status.percent ?? 0}%`}
            {status.status === 'downloaded' && 'Update ready to install'}
          </div>
          <div className="mt-0.5 text-xs text-white/50">
            {status.status === 'available' && "You can keep using the kiosk — update whenever it's convenient."}
            {status.status === 'downloading' && 'This runs in the background.'}
            {status.status === 'downloaded' && 'Restart to finish installing.'}
          </div>
          <div className="mt-3 flex gap-2">
            {status.status === 'available' && (
              <>
                <button
                  onClick={() => window.electronAPI?.startUpdateDownload()}
                  className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Update Now
                </button>
                <button
                  onClick={() => setDismissed(true)}
                  className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium hover:bg-white/20 transition-colors"
                >
                  Later
                </button>
              </>
            )}
            {status.status === 'downloaded' && (
              <>
                <button
                  onClick={() => window.electronAPI?.installUpdateNow()}
                  className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Restart Now
                </button>
                <button
                  onClick={() => setDismissed(true)}
                  className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium hover:bg-white/20 transition-colors"
                >
                  Later
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
